import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Container, Row } from 'reactstrap';
import config from '../../../../../config';
import TableBuilder from '../../../../../shared/components/table/TableBuilder';
import { currencyFormatter, getExportPrefix } from '../../../../../shared/components/table/functions';
import CardPanel from '../../../../../shared/components/widgets/CardPanel';
import { doFetch } from '../../../../../helpers';

function GoogleCatalogMgr() {
  const selectedSeller = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);
  const [agGrid, setAgGrid] = useState(null);

  // Cursor cache for Google's pagination (offset -> cursor mapping)
  const cursorCacheRef = useRef({ 0: null });

  const pageSize = 100;

  const exportPrefix = getExportPrefix('google-catalog', selectedSeller, true);

  const columnDefs = [
    {
      field: 'image_link',
      headerName: 'Image',
      width: 100,
      cellRenderer: (params) => {
        if (params.data?.image_link) {
          return (
            <img
              src={params.data.image_link}
              alt="Product"
              style={{ maxWidth: '75px', maxHeight: '75px', objectFit: 'contain' }}
            />
          );
        }
        return '';
      },
    },
    {
      field: 'item_id',
      headerName: 'Item ID',
      cellRenderer: (params) => {
        if (params.data?.link) {
          return (
            <a
              href={params.data.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              {params.data.item_id}
            </a>
          );
        }
        return params.data?.item_id || '';
      },
    },
    { field: 'sku', headerName: 'SKU' },
    { field: 'title', headerName: 'Title' },
    { field: 'description', headerName: 'Description' },
    { field: 'status', headerName: 'Status' },
    { field: 'availability', headerName: 'Availability' },
    { field: 'price', headerName: 'Price', valueFormatter: currencyFormatter },
    { field: 'sale_price', headerName: 'Sale Price', valueFormatter: currencyFormatter },
    { field: 'currency_code', headerName: 'Currency' },
    { field: 'sell_on_google_quantity', headerName: 'Quantity' },
    { field: 'ad_status', headerName: 'Ad Status' },
    { field: 'reporting_visibility', headerName: 'Reporting Visibility' },
    { field: 'budget_group_id', headerName: 'Budget Group ID' },
    { field: 'brand', headerName: 'Brand' },
    { field: 'brand_override', headerName: 'Brand Override' },
    { field: 'product_type', headerName: 'Product Type' },
    { field: 'google_product_category', headerName: 'Category' },
    { field: 'subcategory_override', headerName: 'Subcategory Override' },
    { field: 'approved_countries', headerName: 'Approved Countries' },
    { field: 'issue_count', headerName: 'Issue Count' },
    { field: 'issue_codes', headerName: 'Issue Codes' },
    { field: 'gtin', headerName: 'GTIN', hide: true },
    { field: 'mpn', headerName: 'MPN', hide: true },
    { field: 'item_group_id', headerName: 'Item Group ID', hide: true },
    { field: 'condition_type', headerName: 'Condition', hide: true },
    { field: 'merchant_id', headerName: 'Merchant ID', hide: true },
    { field: 'merchant_item_id', headerName: 'Merchant Item ID', hide: true },
    { field: 'resource_name', headerName: 'Resource Name', hide: true },
    { field: 'content_language', headerName: 'Language', hide: true },
    { field: 'feed_label', headerName: 'Feed Label', hide: true },
    { field: 'data_source', headerName: 'Data Source', hide: true },
    { field: 'additional_image_count', headerName: 'Additional Images', hide: true },
    { field: 'segment_one', headerName: 'Segment One', hide: true },
    { field: 'segment_two', headerName: 'Segment Two', hide: true },
    { field: 'segment_three', headerName: 'Segment Three', hide: true },
    { field: 'segment_four', headerName: 'Segment Four', hide: true },
    { field: 'segment_five', headerName: 'Segment Five', hide: true },
    { field: 'creation_date', headerName: 'Created', hide: true },
    { field: 'last_update_date', headerName: 'Google Last Updated', hide: true },
    { field: 'google_expiration_date', headerName: 'Google Expiration', hide: true },
    { field: 'updated_at', headerName: 'Last Synced' },
  ];

  const defaultColDef = {
    sortable: true,
    filter: false,
    resizable: true,
    cellStyle: (params) => {
      if (params && params.value < 0.00) {
        return { color: 'red' };
      }
      return null;
    },
  };

  const tableConfig = {
    showDatePicker: false,
    showFooter: false,
    parentControlledLoad: true,
    apiEndPoint: `${config.serverUrl}/google/catalogs/${selectedSeller.value}`,
    paginationPageSize: pageSize,
    cacheBlockSize: pageSize,
  };

  // Helper function to fetch a specific page with cursor
  const fetchGooglePage = async (cursor, headerWithAuth) => {
    const url = `${tableConfig.apiEndPoint}?limit=${pageSize}${cursor ? `&after=${cursor}` : ''}`;
    const requestOptions = {
      method: 'GET',
      headers: headerWithAuth,
    };
    return doFetch(url, requestOptions);
  };

  const datasource = {
    getRows: async (params) => {
      const { request, success, fail } = params;
      const { startRow } = request;
      const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };

      const offset = startRow || 0;

      try {
        // Get cursor for this page
        const cursor = cursorCacheRef.current[offset];

        // Only forward navigation is supported (cursor is cached from previous page)
        if (offset > 0 && cursor === undefined) {
          console.warn('Google: Missing cursor for offset', offset);
          fail();
          return;
        }

        const data = await fetchGooglePage(cursor, headerWithAuth);

        // Cache next cursor if available
        if (data.paging?.hasNext && data.paging?.cursors?.after) {
          cursorCacheRef.current[offset + pageSize] = data.paging.cursors.after;
        }

        // Use total count from API if available to avoid infinite scroll loops
        const rowCount = data.total || (data.results ? data.results.length : 0);

        success({
          rowData: data.results || [],
          rowCount,
        });
      } catch (error) {
        console.error('Google catalog fetch error:', error);
        fail();
      }
    },
  };

  const parentAccess = (agGridPointer) => {
    setAgGrid(agGridPointer);
  };

  useEffect(() => {
    if (agGrid) {
      cursorCacheRef.current = { 0: null };
      agGrid.refreshData();
    }
  }, [selectedSeller.value, agGrid]);

  return (
    <Container className="todo-app">
      <Row>
        <CardPanel
          title="Google Catalog"
          subhead={new Date().toISOString()}
          autorenewButton={false}
          closeButton={false}
        >
          <TableBuilder
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            apiEndPoint={tableConfig.apiEndPoint}
            showDatePicker={tableConfig.showDatePicker}
            showFooter={tableConfig.showFooter}
            rowHeight={75}
            pagination
            rowModelType="serverSide"
            datasource={datasource}
            paginationPageSize={tableConfig.paginationPageSize}
            cacheBlockSize={tableConfig.cacheBlockSize}
            serverSideInfiniteScroll={false}
            parentControlledLoad={tableConfig.parentControlledLoad}
            editSelectedRowsButton={false}
            showImportButton={false}
            parentAccess={parentAccess}
            exportPrefix={exportPrefix}
          />
        </CardPanel>
      </Row>
    </Container>
  );
}

export default GoogleCatalogMgr;
